import {
  RequireAuth,
  useFusionAuth,
  type UserInfo,
} from "@fusionauth/react-sdk";
import React, { useContext, useEffect } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  NavBarContext,
  NavBarDispatchContext,
} from "./contexts/NavBarContext.tsx";
import {
  ProjectContext,
  ProjectDispatchContext,
} from "./contexts/ProjectContext.tsx";
import NotFound from "./pages/NotFound.tsx";
import Agreements from "./pages/authentication/Agreements";
import AuthLoading from "./pages/authentication/AuthLoading";
import CompanySelection from "./pages/authentication/CompanySelection";
import Home from "./pages/home/Home.tsx";
import ProjectDetails from "./pages/project/ProjectDetails";
import BaseSettingsPage from "./pages/settings/BaseSettingsPage";
import { useFetch } from "./services/hooks/useFetch.ts";
import { deviceApiPrefix } from "./utils/main.ts";

export default function AppRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const project = useContext(ProjectContext);
  const dispatch = useContext(ProjectDispatchContext);
  const { company, showCompanySwitcher } = useContext(NavBarContext);
  const navBarDispatch = useContext(NavBarDispatchContext);
  const { isLoggedIn, isFetchingUserInfo, startLogin, userInfo } =
    useFusionAuth<UserInfo>();
  const { fetch: fetchCompany } = useFetch<Company>();
  const agreement_type = import.meta.env.VITE_AGREEMENT_TYPE || window._env_?.AGREEMENT_TYPE || "invalid agreement type";

  const agreementsAccepted =
    userInfo &&
    userInfo.agreements_accepted &&
    userInfo.agreements_accepted.ALL &&
    userInfo.agreements_accepted[agreement_type];

  useEffect(() => {
    if (isLoggedIn) {
      if (isFetchingUserInfo || !userInfo) {
        return;
      }

      if (userInfo.multiGroup && !showCompanySwitcher) {
        navBarDispatch({ type: "showCompanySwitcher" });
      }

      localStorage.setItem(
        "settings",
        userInfo.settings ? JSON.stringify(userInfo.settings) : "{}",
      );

      if (location.pathname !== "/agreements" && !agreementsAccepted) {
        // if user hasn't accepted relevant env terms, lock into agreements page
        navigate("/agreements");
      } else if (location.pathname === "/agreements" && agreementsAccepted) {
        navigate("/");
      } else if (location.pathname !== "/company" && !userInfo.companyId) {
        // no selected company ID
        navigate("/company");
      } else if (!projectId && project?.id) {
        // if we aren't on a project-specific page, but have a project loaded in context then delete it
        dispatch({ type: "delete" });
      }
    } else {
      if (location.pathname === "/login") {
        startLogin();
      } else {
        navigate("/login");
      }
    }
  }, [
    isLoggedIn,
    navigate,
    location,
    startLogin,
    agreementsAccepted,
    isFetchingUserInfo,
    userInfo,
    projectId,
    project?.id,
    dispatch,
    navBarDispatch,
    showCompanySwitcher,
  ]);

  useEffect(() => {
    if (
      isLoggedIn &&
      !isFetchingUserInfo &&
      userInfo &&
      userInfo.companyId &&
      (!company || company.id !== userInfo.companyId)
    ) {
      void fetchCompany(`${deviceApiPrefix}/companies/${userInfo.companyId}`, {
        postResponseAction: (companyData) => {
          navBarDispatch({ type: "setCompany", company: companyData });
        },
      });
    }
  }, [
    isLoggedIn,
    isFetchingUserInfo,
    userInfo,
    company,
    fetchCompany,
    navBarDispatch,
  ]);

  return (
    <>
      {!isLoggedIn ||
        (isLoggedIn && (isFetchingUserInfo || !userInfo) && <PublicRoutes />)}
      {isLoggedIn &&
        !isFetchingUserInfo &&
        userInfo &&
        (userInfo.companyId ? <PrivateRoutes /> : <CompanySelection />)}
    </>
  );
}

function PublicRoutes() {
  return (
    <Routes>
      <Route path="*" element={<AuthLoading />} />
    </Routes>
  );
}

function PrivateRoutes() {
  return (
    <Routes>
      <Route
        path={"/details"}
        element={
          <RequireAuth>
            <ProjectDetails />
          </RequireAuth>
        }
      />
      <Route
        path={"/"}
        element={
          <RequireAuth>
            <Home />
          </RequireAuth>
        }
      />
      <Route
        path={"/settings"}
        element={
          <RequireAuth>
            <BaseSettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path={"/agreements"}
        element={
          <RequireAuth>
            <Agreements />
          </RequireAuth>
        }
      />
      <Route
        path={"/company"}
        element={
          <RequireAuth>
            <CompanySelection />
          </RequireAuth>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
